/**
 *
 * "Admin page with link to other admin action pages"
 *
 * @file   AdminPage.js
 * @author Lateral
 * @since  2023
 */
import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { Link, Outlet } from 'react-router-dom'
import { Stack, Typography } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { defaultRoutes } from 'pages'
import { ConfirmationDialog, Header, SCOPES } from 'components'
import { useCurrentUser } from 'hooks'
import { useWearAppAdminRequests } from 'hooks/maintenance/useWearAppAdminRequests'
import { useSnackbar } from 'notistack'
import { CircularProgress } from '@mui/material'

import { LoadingButton } from '@mui/lab'

function LinkTab(props) {
  return <Tab sx={{ pl: 0, pr: 2 }} component={Link} {...props} />
}

const AdminPage = () => {
  /**
   * Generates a page vewing admin links
   *
   * @const
   *
   * @returns {object} - React app page
   */
  const location = useLocation()
  const { currentUser } = useCurrentUser()
  const hasSites = currentUser?.hasAllScopes([SCOPES.siteManagement.Read])

  const { enqueueSnackbar } = useSnackbar()
  const { startServices, stopServices, getAuthorisation, wearAppAvailabilityRequest } = useWearAppAdminRequests()
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const authorisation = getAuthorisation()

  const [value, setValue] = useState(0)

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const data = authorisation.data?.data
  const isWearAppAvailable = wearAppAvailabilityRequest({
    refetchInterval: data?.status === 'started' ? 1 * 60 * 1000 : false
  })
  const wearAdminButtonText = useMemo(() => {
    if (authorisation.isInitialLoading || !data) {
      return ''
    }
    if (data.status === 'deleted') {
      return 'Start WearApp services'
    }
    return 'Stop WearApp services'
  }, [data, authorisation.isInitialLoading])

  useEffect(() => {
    let initialValue = hasSites

    if (location.pathname === '/admin/accounts/roles') {
      initialValue = 0
    } else if (location.pathname === '/admin/accounts') {
      initialValue = hasSites
    }

    setValue(initialValue)
  }, [hasSites])

  const routes = useMemo(() => {
    return defaultRoutes({
      isAdmin: currentUser.isAdmin,
      canAccessRoles: currentUser.canAccessRoles,
      canAccessUsers: currentUser.canAccessUsers
    })
  }, [currentUser.isAdmin])

  const isLaoding =
    loading ||
    authorisation.isFetching ||
    (data?.status === 'started' && !isWearAppAvailable.data) ||
    (data?.status === 'deleted' && isWearAppAvailable.data)

  return (
    <Box sx={{ width: '100%', height: '100%', px: '15px' }}>
      <Header routes={routes} />

      <Stack sx={{ pt: 2 }} direction="row" alignItems="center">
        <Typography variant="h1">Admin</Typography>
        {authorisation.isInitialLoading ? (
          <CircularProgress
            size={20}
            sx={{
              ml: 1
            }}
            color="secondary"
          />
        ) : authorisation.isFetched && authorisation.status ? (
          <LoadingButton
            loading={isLaoding}
            onClick={() => setShowConfirmationDialog(true)}
            variant="contained"
            size="small"
            color="secondary"
            sx={{
              ml: '1em'
            }}>
            {wearAdminButtonText}
          </LoadingButton>
        ) : null}
      </Stack>
      <Tabs value={value} onChange={handleChange} aria-label="nav tabs example">
        {hasSites ? <LinkTab label="Manage Sites" to="sites" /> : null}
        <LinkTab label="Account" to="" />
      </Tabs>
      <Outlet />

      {showConfirmationDialog && (
        <ConfirmationDialog
          saveButtonProps={{
            color: 'error'
          }}
          text={`Are you sure you want to ${data.status === 'deleted' ? 'start' : 'stop'} WearApp services?`}
          onClose={async (result) => {
            if (result === true) {
              setLoading(true)
              const isDelete = data.status !== 'deleted'
              const fn = isDelete ? stopServices : startServices
              fn.mutateAsync()
                .then(() => {
                  authorisation.refetch()
                  enqueueSnackbar({
                    message: `WearApp services have been queued to be ${
                      data.status === 'deleted' ? 'started' : 'stopped'
                    }`
                  })
                })
                .catch(() => {
                  enqueueSnackbar({
                    message: `An error occurred`
                  })
                })
                .finally(() => {
                  setLoading(false)
                })
            }
            setShowConfirmationDialog(false)
          }}
        />
      )}
    </Box>
  )
}
export default AdminPage
